<script>
import isArray from 'lodash/isArray';
import indexOf from 'lodash/indexOf';
import isString from 'lodash/isString';
import { authComputed, authMethods, actAsComputed, actAsMethods } from '@state/helpers';
import { PtTopBar, PtSideBar, PtNavItem } from '@pathao/ui-components/esm/NavBar';
import Logo from './Logo';
import ProfileCard from './ProfileCard';
import router from '../../router';
import Config from '../../config';

export default {
  name: 'TopNav',
  components: { Logo, PtTopBar, PtSideBar, PtNavItem, ProfileCard },
  data() {
    const isCaptainManagementPage = this.$route.path.startsWith('/captain-management/');
    const isProfileManagementPage = this.$route.path.startsWith('/profile-management/');
    const isActivityLogPage = this.$route.path.startsWith('/activity-log/');
    const isPartnersPage = this.$route.path.startsWith('/partners/');

    return {
      isCaptainManagementPage,
      isProfileManagementPage,
      isActivityLogPage,
      isPartnersPage,
      USER_ROLES: Config.USER_ROLES,
      showMobileMenu: window.innerWidth < 992,
      isHomeAccordionOpen: isCaptainManagementPage,
      isProfileAccordionOpen: isProfileManagementPage,
    };
  },
  computed: {
    ...authComputed,
    ...actAsComputed,
  },
  mounted() {
    if (this.$refs.ptSideBar) {
      const el = this.$refs.ptSideBar.$el;

      el.classList.remove('pt-sidebar-content');
    }

    this.$nextTick(() => {
      window.addEventListener('resize', () => {
        this.showMobileMenu = window.innerWidth < 992;
      });
    });
  },
  methods: {
    ...authMethods,
    ...actAsMethods,
    logMeOut() {
      this.logOut()
        .then(() => {
          router.go('login');
        });
    },
    matchesUrl(path) {
      if (isArray(path)) {
        return indexOf(path, this.$route.path) > -1;
      }

      return this.$route.path === path;
    },
    matchesUserRole(routeRoles) {
      const isLoggedIn = this.$store.getters['auth/loggedIn'];

      if (!isLoggedIn) {
        this.logMeOut();
      }

      const userRoles = this.$store.getters['auth/roles'];

      let hasCorrectRole = false;

      if (isArray(routeRoles)) {
        for (let id = 0; id < routeRoles.length; id++) {
          if (indexOf(userRoles, routeRoles[id]) > -1) {
            hasCorrectRole = true;
            break;
          }
        }
      } else if (isString(routeRoles)) {
        hasCorrectRole = indexOf(userRoles, routeRoles) > -1;
      }

      return hasCorrectRole;
    },
    stopActing() {
      this.stopAct()
        .then(() => {
          this.$router.push({ name: 'login' });
        })
        .catch(() => {
          this.$notification.error('Cannot stop acting');
        });
    },
  },
};
</script>

<template>
  <div>
    <pt-top-bar>
      <pt-side-bar ref="ptSideBar">
        <div
          v-if="showMobileMenu"
          class="pt-mobile-menu"
        >
          <pt-nav-item
            v-if="matchesUserRole(USER_ROLES.SUPER_ADMIN) && !matchesUserRole(USER_ROLES.PARTNER)"
            :selected="matchesUrl('/activity-log/list')"
            to="/activity-log/list"
          >Home</pt-nav-item>
          <div v-if="matchesUserRole(USER_ROLES.PARTNER)">
            <div
              class="flex items-center pl-8 h-12 uppercase font-bold cursor-pointer text-black"
              @click="isHomeAccordionOpen = !isHomeAccordionOpen"
            >
              Home
              <i :class="{ 'fa ml-4': true, 'fa-chevron-down': isHomeAccordionOpen, 'fa-chevron-up': !isHomeAccordionOpen }" />
            </div>
            <div
              v-if="isHomeAccordionOpen"
              class="collapsible-links"
            >
              <pt-nav-item
                :selected="matchesUrl('/captain-management/drivers')"
                to="/captain-management/drivers"
              >Drivers
              </pt-nav-item>
              <pt-nav-item
                :selected="matchesUrl('/captain-management/performance')"
                to="/captain-management/performance"
              >Performance
              </pt-nav-item>
              <pt-nav-item
                :selected="matchesUrl('/captain-management/trips')"
                to="/captain-management/trips"
              >Trips
              </pt-nav-item>
              <pt-nav-item
                :selected="matchesUrl('/captain-management/payments')"
                to="/captain-management/payments"
              >Earnings
              </pt-nav-item>
              <pt-nav-item
                :selected="matchesUrl('/captain-management/live-map')"
                to="/captain-management/live-map"
              >Live Map
              </pt-nav-item>
              <pt-nav-item
                :selected="matchesUrl('/captain-management/payment-statements')"
                to="/captain-management/payment-statements"
              >Payment Statement
              </pt-nav-item>
            </div>
          </div>

          <pt-nav-item
            v-if="matchesUserRole(USER_ROLES.PARTNER) && matchesUserRole(USER_ROLES.SUPER_ADMIN)"
            :selected="matchesUrl('/activity-log/list')"
            to="/activity-log/list"
          >Activity Log
          </pt-nav-item>

          <pt-nav-item
            v-if="matchesUserRole(USER_ROLES.ADMIRAL_ADMIN)"
            :selected="matchesUrl('/partners/list')"
            to="/partners/list"
          >Partners
          </pt-nav-item>

          <div v-if="matchesUserRole([USER_ROLES.PARTNER, USER_ROLES.SUPER_ADMIN])">
            <div
              class="flex items-center pl-8 h-12 uppercase font-bold cursor-pointer text-black"
              @click="isProfileAccordionOpen = !isProfileAccordionOpen">
              Profile
              <i :class="{ 'fa ml-4': true, 'fa-chevron-down': isProfileAccordionOpen, 'fa-chevron-up': !isProfileAccordionOpen }" />
            </div>
            <div
              v-if="isProfileAccordionOpen"
              class="collapsible-links"
            >
              <pt-nav-item
                :selected="matchesUrl('/profile-management/profile')"
                to="/profile-management/profile"
              >My Profile
              </pt-nav-item>
              <pt-nav-item
                v-if="matchesUserRole(USER_ROLES.PARTNER)"
                :selected="matchesUrl('/profile-management/vehicles')"
                to="/profile-management/vehicles"
              >Vehicles
              </pt-nav-item>
              <pt-nav-item
                :selected="matchesUrl('/profile-management/pairing')"
                to="/profile-management/pairing"
              >Pairing
              </pt-nav-item>
            </div>
          </div>
          <div class="border-b border-grey-light mx-4" />
          <pt-nav-item
            v-if="matchesUserRole([USER_ROLES.PARTNER, USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMIRAL_ADMIN]) && !isActingUser"
            :selected="matchesUrl('/change-password')"
            to="/change-password"
          >Reset Password
          </pt-nav-item>
          <!-- We might need to get back to logout in sidebar again -->
          <pt-nav-item to="/logout">Log Out</pt-nav-item>
        </div>

        <div v-else>
          <pt-nav-item
            v-if="matchesUserRole(USER_ROLES.SUPER_ADMIN) && !matchesUserRole(USER_ROLES.PARTNER)"
            :selected="matchesUrl('/activity-log/list')"
            to="/activity-log/list"
          >Home</pt-nav-item>
          <pt-nav-item
            v-if="matchesUserRole(USER_ROLES.PARTNER)"
            :selected="matchesUrl('/captain-management/drivers')"
            to="/captain-management/drivers"
            class="flex flex-col res-menu"
            style="height: auto; min-height:3rem"
          >Home
          </pt-nav-item>
          <pt-nav-item
            v-if="matchesUserRole(USER_ROLES.PARTNER) && matchesUserRole(USER_ROLES.SUPER_ADMIN)"
            :selected="matchesUrl('/activity-log/list')"
            to="/activity-log/list"
          >Activity Log</pt-nav-item>
          <pt-nav-item
            v-if="matchesUserRole(USER_ROLES.ADMIRAL_ADMIN)"
            :selected="matchesUrl('/partners/list')"
            to="/partners/list"
          >Partners</pt-nav-item>
          <pt-nav-item
            v-if="matchesUserRole([USER_ROLES.PARTNER, USER_ROLES.SUPER_ADMIN])"
            :selected="matchesUrl('/profile-management/profile')"
            to="/profile-management/profile"
          >Profile
          </pt-nav-item>
          <div class="border-b border-grey-light mx-4" />
          <pt-nav-item
            v-if="matchesUserRole([USER_ROLES.PARTNER, USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMIRAL_ADMIN]) && !isActingUser"
            :selected="matchesUrl('/change-password')"
            to="/change-password"
          >Reset Password
          </pt-nav-item>
        </div>
      </pt-side-bar>
      <logo class="brand-logo" />
      <profile-card
        :user="currentUser"
        :is-acting="isActingUser"
        class="sm:hidden lg:flex"
        @profile-logout="logMeOut"
        @stop-acting="stopActing"
      />
    </pt-top-bar>
    <div class="flex border-b border-grey-light mb-2 lg:hidden" />
    <pt-top-bar
      v-if="isCaptainManagementPage && matchesUserRole(USER_ROLES.PARTNER)"
      class="sm:hidden lg:flex"
    >
      <pt-nav-item
        :selected="matchesUrl('/captain-management/drivers')"
        to="/captain-management/drivers"
      >Drivers
      </pt-nav-item>
      <pt-nav-item
        :selected="matchesUrl('/captain-management/performance')"
        to="/captain-management/performance"
      >Performance
      </pt-nav-item>
      <pt-nav-item
        :selected="matchesUrl('/captain-management/trips')"
        to="/captain-management/trips"
      >Trips
      </pt-nav-item>
      <pt-nav-item
        :selected="matchesUrl('/captain-management/payments')"
        to="/captain-management/payments"
      >Earnings
      </pt-nav-item>
      <pt-nav-item
        :selected="matchesUrl('/captain-management/live-map')"
        to="/captain-management/live-map"
      >Live Map
      </pt-nav-item>
      <pt-nav-item
        :selected="matchesUrl('/captain-management/payment-statements')"
        to="/captain-management/payment-statements"
      >Payment Statement
      </pt-nav-item>
    </pt-top-bar>
    <pt-top-bar
      v-if="isProfileManagementPage && matchesUserRole([USER_ROLES.PARTNER, USER_ROLES.SUPER_ADMIN])"
      class="sm:hidden lg:flex"
    >
      <pt-nav-item
        :selected="matchesUrl('/captain-management/drivers')"
        to="/captain-management/drivers"
      >Home
      </pt-nav-item>
      <pt-nav-item
        :selected="matchesUrl('/profile-management/profile')"
        to="/profile-management/profile"
      >Profile
      </pt-nav-item>
      <pt-nav-item
        v-if="matchesUserRole(USER_ROLES.PARTNER)"
        :selected="matchesUrl('/profile-management/vehicles')"
        to="/profile-management/vehicles">Vehicles
      </pt-nav-item>
      <pt-nav-item
        :selected="matchesUrl('/profile-management/pairing')"
        to="/profile-management/pairing"
      >Pairing
      </pt-nav-item>
    </pt-top-bar>
    <pt-top-bar
      v-if="isActivityLogPage && matchesUserRole(USER_ROLES.SUPER_ADMIN)"
      class="sm:hidden lg:flex"
    >
      <pt-nav-item
        :selected="matchesUrl('/activity-log/list')"
        to="/activity-log/list">Activity List</pt-nav-item>
    </pt-top-bar>
  </div>
</template>
